/**
 * Under the call flow map: can the AI follow it, what is wrong with it, and
 * the course the AI would read.
 *
 * Three states, each saying what it means for the calls, because a map that
 * autosaves looks saved whether or not it can be followed:
 *  - nothing drawn: the campaign talks by its goal, as before;
 *  - followable: how many steps and how much of the course's budget it uses,
 *    with the course itself one press away;
 *  - broken: every problem named against its step or arrow (the same steps are
 *    red on the canvas), and that the campaign cannot be activated and places
 *    no call until it is fixed.
 */
import { useState } from "react";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import type { FlowMapDoc } from "@wa/widgets/flow-map/model";
import { useT } from "../../../../../i18n/I18nProvider";
import { problemKey, problemSubject, type CallFlowCheck, type CallFlowProblem } from "../../../../../lib/aiCallFlow";
import { Btn, fmtNum } from "../../../../ui";
import { Note } from "../../../settings/panel/fields";
import { currentCheck, type FlowCheckState } from "./useFlowCheck";

function ProblemLine({ problem, doc }: { problem: CallFlowProblem; doc: FlowMapDoc }) {
  const t = useT();
  const subject = problemSubject(problem, doc, t("aiCalls.flow.untitled"));
  const where =
    subject.kind === "node"
      ? t("aiCalls.flow.subject.node", { title: subject.title })
      : subject.kind === "arrow"
        ? t("aiCalls.flow.subject.arrow", { from: subject.from, to: subject.to })
        : t("aiCalls.flow.subject.map");
  return (
    <li className="flex gap-2">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
      <span>
        <span className="font-medium">{where}:</span>{" "}
        {t(`aiCalls.flow.problem.${problemKey(problem.code)}`, {
          limit: problem.limit ?? "",
          placeholder: problem.placeholder ?? "",
          code: problem.code,
        })}
      </span>
    </li>
  );
}

function CheckBody({ check, doc, active }: { check: CallFlowCheck; doc: FlowMapDoc; active: boolean }) {
  const t = useT();
  const [showCourse, setShowCourse] = useState(false);

  if (check.empty) return <Note tone="info">{t("aiCalls.flow.check.empty")}</Note>;

  if (!check.ok) {
    const tooLong = check.problems.find((p) => p.code === "too_long");
    return (
      <div className="space-y-3">
        <Note tone="bad" live>
          {t("aiCalls.flow.check.problems", { n: check.problems.length })}
          {active ? ` ${t("aiCalls.flow.check.activeBroken")}` : ""}
        </Note>
        <ul className="space-y-1.5 text-sm">
          {check.problems.map((problem, i) => (
            // By position: one node can carry the same code twice (two unknown placeholders).
            <ProblemLine key={`${i}-${problem.code}`} problem={problem} doc={doc} />
          ))}
        </ul>
        {tooLong ? (
          <p className="crm-sub text-xs">{t("aiCalls.flow.check.chars", { chars: fmtNum(check.chars), limit: fmtNum(check.limit) })}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="size-4 shrink-0 text-success" />
          {t("aiCalls.flow.check.ok", { n: check.steps.length, chars: fmtNum(check.chars), limit: fmtNum(check.limit) })}
        </p>
        <Btn small onClick={() => setShowCourse((v) => !v)} aria-expanded={showCourse}>
          {showCourse ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          {t(showCourse ? "aiCalls.flow.check.hideCourse" : "aiCalls.flow.check.showCourse")}
        </Btn>
      </div>
      {showCourse ? (
        <div className="space-y-1.5">
          <p className="crm-sub text-xs">{t("aiCalls.flow.check.courseHint")}</p>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs leading-relaxed">
            {check.text}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

export function FlowCheckPanel({ result, doc, active }: { result: FlowCheckState; doc: FlowMapDoc; active: boolean }) {
  const t = useT();
  const check = currentCheck(result);
  return (
    <div className="space-y-2 border-t border-border pt-4">
      {check ? <CheckBody check={check} doc={doc} active={active} /> : null}
      {result.state === "checking" && !check ? (
        <p className="crm-sub flex items-center gap-2 text-sm">
          <Loader2 className="size-3.5 animate-spin" />
          {t("aiCalls.flow.check.checking")}
        </p>
      ) : null}
      {result.state === "failed" ? <Note tone="warn">{t("aiCalls.flow.check.failed")}</Note> : null}
    </div>
  );
}
